/**
 * The shape of a statement, and the order the report states them in.
 *
 * This lives outside `catalogue.generated.ts` because that file is rewritten
 * from the backend every time `scripts/generate-ecg-statements.js` runs, and
 * three things have to survive that:
 *
 *   - the section list, which now includes sections the backend has no keys
 *     for at all (`hypertrophy`, `axis`);
 *   - `shortcuts`, which is a review-screen concern the report generator knows
 *     nothing about;
 *   - `origin`, which records whether a statement's wording came from the
 *     backend catalogue or was written for this app.
 */

export type StatementLanguage = "en" | "it" | "uk";

export type StatementSectionId =
    | "heartRhythm"
    | "avConduction"
    | "atrialHypertrophy"
    | "hypertrophy"
    | "qWaves"
    | "ivConduction"
    | "axis"
    | "repolarization"
    | "technical";

/**
 * The order a 12-lead resting report states its findings in: rhythm, then
 * conduction from the atria down, then chamber size, then the QRS itself, then
 * repolarisation, and finally anything about the recording rather than the
 * patient.
 */
export const STATEMENT_SECTION_ORDER: StatementSectionId[] = [
    "heartRhythm",
    "avConduction",
    "atrialHypertrophy",
    "hypertrophy",
    "qWaves",
    "ivConduction",
    "axis",
    "repolarization",
    "technical",
];

/**
 * Where a statement's wording comes from.
 *
 * `backend` means it was generated from `restEcgReport*.properties` and is
 * word-for-word what the algorithm and the printed report use. `local` means it
 * was written for this screen because the backend has no key for it — the
 * wording is ours, and a clinician should review it before it is trusted the
 * way the backend text is.
 */
export type StatementOrigin = "backend" | "local";

export type EcgStatement = {
    /** Stable id, e.g. `heartRhythm.aFibrillation`. */
    key: string
    section: StatementSectionId
    /** True when the text carries a `%s` slot for lead names. */
    leads: boolean
    text: {[language in StatementLanguage]: string}
    /**
     * Abbreviations a technician may type instead of the words.
     *
     * The backend statements need none: their key tails are already the codes
     * (`LAFB`, `SVEB`, `RBBB`), so those match for free. Statements written for
     * this app carry theirs explicitly, because a key tail like
     * `aFlutter2to1` is not what anyone types.
     */
    shortcuts?: string[]
    origin?: StatementOrigin
}
